import { useEffect } from "react";
import {
  BulkSignOrchestratorApiTypes,
  bulkSignOrchestratorApi,
} from "@rshbintech.dboul/http";

import { processError } from "../../utils/process-error";

import {
  DOCUMENT_SIGNING_STAGES,
  SIGNING_STATUSES,
  SIGNING_TYPES,
  TASK_STATUS,
} from "../../constants";
import { UseCheckLimitsOptions } from "./types";

export const useCheckLimits = ({
  isOpened,
  isPluginInit,
  isSigning,
  unsignedDocuments,
  setError,
  setDocumentsToView,
  setTaskId,
  setStatus,
}: UseCheckLimitsOptions) => {
  useEffect(() => {
    if (isOpened && isPluginInit) {
      const documentsForAddingTaskRequest: BulkSignOrchestratorApiTypes.DocumentReq[] =
        [];

      unsignedDocuments.forEach((document) => {
        const documentForAddingTask: BulkSignOrchestratorApiTypes.DocumentReq =
          {
            id: document.id,
            type: document.type.code,
            system: document.systemShortName,
            status: document.status,
          };

        if (document?.signingUnavailableReason) {
          documentForAddingTask.errorText = document.signingUnavailableReason;
        }

        documentsForAddingTaskRequest.push(documentForAddingTask);
      });

      const checkLimits = async (
        documents: BulkSignOrchestratorApiTypes.DocumentReq[]
      ) => {
        try {
          const signingType = isSigning
            ? SIGNING_TYPES.SIGN
            : SIGNING_TYPES.VISE;

          const localTaskId = (
            await bulkSignOrchestratorApi.api.addTask({
              documents,
              signingType,
            })
          ).data;

          isSigning
            ? await bulkSignOrchestratorApi.api.verifySignTask(localTaskId)
            : await bulkSignOrchestratorApi.api.verifyViseTask(localTaskId);

          const signTaskStatusResponse =
            await bulkSignOrchestratorApi.api.getSignTaskStatus(localTaskId);

          const { state: taskStatus, errorText } = signTaskStatusResponse.data;

          if (taskStatus !== TASK_STATUS.VERIFIED) {
            if (errorText) {
              setError(errorText);

              return;
            }

            throw new Error("Не удалось верифицировать задание");
          }

          //  проводим проверку на готовность документов к подписанию

          const documentsToCheckStatuses: BulkSignOrchestratorApiTypes.DocumentsStatusReq[] =
            unsignedDocuments.map((document) => ({
              id: document.id,
              type: document.type.code,
              system: document.systemShortName,
            }));

          const getDocumentsStatusesResult =
            await bulkSignOrchestratorApi.api.getDocumentsStatuses(
              localTaskId,
              {
                documents: documentsToCheckStatuses,
              }
            );

          const { documents: documentsWithActualStatuses = [] } =
            getDocumentsStatusesResult.data;

          setDocumentsToView((prevDocuments) =>
            prevDocuments.map((document) => {
              const actualValue = documentsWithActualStatuses.find(
                ({ id }) => id === document.id
              );

              if (actualValue?.errorText) {
                return {
                  ...document,
                  documentStatus: {
                    ...document.documentStatus,
                    errorText: actualValue.errorText,
                  },
                  signatureInfo: {
                    ...document.signatureInfo,
                    signingStage: DOCUMENT_SIGNING_STAGES.FINISHED_WITH_ERROR,
                  },
                };
              }

              return document;
            })
          );

          setTaskId(localTaskId);
          setStatus(SIGNING_STATUSES.READY_TO_BE_SIGNED);
        } catch (error) {
          processError(error, setError);
        }
      };

      checkLimits(documentsForAddingTaskRequest);
    }
  }, [isOpened, unsignedDocuments, isPluginInit]);
};
